import React, { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import Api from "../../services/Api";
import {useTranslation} from "react-i18next";
import {getLanguage} from "../../lang/helpers";
import {RecordingInterface} from "./interfaces";
import CustomButton from "../../components/CustomButton";


const TeamRecording: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [query, setQuery] = useState("");
  const [previousQuery, setPreviousQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [recording, setRecording] = useState<RecordingInterface | null>(null);
  const { recordingId } = useParams();

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    api
      .get("client/recording/" + recordingId)
      .then((response) => {
        if (response.json.error) {
          setError(response.json.error);
        } else {
          setRecording(response.json.recording);
          setLoaded(true);
        }
      })
      .catch((error) => {
        alert("Unknown error (http status " + error.response.status + ")");
      });
  };

  const deleteRecording = () => {
    const status = confirm("Are you sure you want to delete this recording?");
    if (status) {
      api.get("client/recording_delete/" + recordingId).then(() => {
        navigate('/team/recordings');
      })
    }
  }

  if (error.length > 0) {
    return <div className="mt-4">Error: {error}</div>;
  }

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }

  if (!recording) {
    return <div className="mt-4">Missing recording - contact support...</div>;
  }

  const transcribe = () => {
    setRecording({...recording, status:2});
    api.get("client/recording_transcribe/" + recordingId).then(response => {
      setRecording(response.json.recording);
    })
  }

  const showStatus = () => {
    if (recording.status === 0) {
      return <div className="z-title2">Recording !?</div>;
    } else if (recording.status === 1) {
      return (
        <div>
          <CustomButton buttonType="blueBase" onClick={() => transcribe()}>
            Translate this recording into text
          </CustomButton>
        </div>
      )
    } else if (recording.status === 2) {
      return <div className="z-body1">Transcribing, please wait. This could take some time...</div>;
    } else if (recording.status === 3) {
      return (
        <div>
          <div className="z-title2">Transcription:</div>
          <div className="z-body1 mt-4">{recording.transcription}</div>
        </div>
      );
    }
  }

  async function handleStream(reader: ReadableStreamDefaultReader) {
    let done, value;
    let streamResponse = "";
    let finalResponse = "";
    const decoder = new TextDecoder();

    while (!done) {
      ({ value, done } = await reader.read());

      if (done) {
        console.log("Closed stream. finalResponse is " + finalResponse.length + " bytes");
        if (finalResponse.length > 0) {
          try {
            // console.log(finalResponse);
            const data = JSON.parse(finalResponse);
            if (data.aiResponse) {
              setAnswer(data.aiResponse);
            } else {
              console.log(data)
            }
          } catch (error) {
            console.log(error);
          }
        }
        return;
      }

      const responseData = decoder.decode(value);

      // console.log("Got data: " + responseData);

      // If we have received start of finalResponse, add to it
      if (finalResponse.length > 0) {
        console.log('Adding ' + responseData.length + " bytes to finalResponse which is " + finalResponse.length);
        finalResponse += responseData;
      } else {
        // Look for position of {"aiResponse". If we find that, this is the start of finalResponse
        const aiResponse = responseData.indexOf('{"aiResponse"');
        if (aiResponse > -1) {
          console.log("Found start of final message at " + aiResponse);
          finalResponse = responseData.substring(aiResponse);
        }
      }

      if (finalResponse.length === 0) {
        if (responseData.startsWith("_-_")) {
          setAnswer(responseData.substring(3));
        } else {
          streamResponse += responseData;
          setAnswer(streamResponse);
        }
      }
    }
  }

  const sendQuery = () => {

    const data = {
      query: query,
      recordingId: recordingId
    }
    setPreviousQuery(query);
    setQuery("");

    api.post("fast_api/client/query_transcription", data, [], false, true)
      .then(response => {
        if (response.response.ok) {
          if (response.response.body) {
            handleStream(response.response.body.getReader()).then(() => {
              console.log('AI done')
            });
          }
        } else {
          alert(response.response.statusText);
        }
      })
      .catch(error =>  {
        // This should be displayed as alert from api handler
        console.log(error);
      });
  }

  const renderQueryAi = () => {
    return (
      <div className="bg-indigo-900 px-4 py-2 mt-2 rounded-md">
        <div className="flex gap-x-2">
          <input type="text" value={query} className="z-input"
                 onChange={(e) => setQuery(e.target.value)}
                 onKeyUp={(e) => e.key === "Enter" && sendQuery()}
                 placeholder="Ask something about this transcription..."></input>
          <CustomButton buttonType="blueBase" size="sm" disabled={query.length === 0} style={{width: "unset"}}
                        onClick={() => sendQuery()}>
            Ask
          </CustomButton>
        </div>

        {
          (answer.length > 0 || previousQuery.length > 0) &&
            <div className="mt-4">
                <p>Q: {previousQuery}</p>
                <p className="mt-4 z-body1 whitespace-pre-line">{answer}</p>
            </div>
        }
      </div>
    )
  }

  const showSize = () => {
    if (recording.lengthBytes < 10000) {
      return recording.lengthBytes + " bytes";
    } else if (recording.lengthBytes < 1000000) {
      return (recording.lengthBytes / 1024).toFixed(0) + " kB";
    } else {
      return (recording.lengthBytes / (1024 * 1024)).toFixed(1) + " MB";
    }
  }

  const showLength = () => {
    return (recording.lengthTime / 1000).toFixed(1) + " s";
  }

  return (
    <>
      <LocalBreadcrumbs crumbs={[
        {name: "Team", url: "/team"},
        {name: "Recordings", url: "/team/recordings"},
        {name: "Recording"},
      ]}/>

      <div className="mt-4 z-heading2">
        Recording
      </div>

      <div className="flex flex-wrap gap-x-4">
        <div className="main-half-col">
          <div className="bg-indigo-900 p-4 mt-2 rounded-md">
            <div className="z-body1">Name: {recording.name}</div>
            <div className="z-body1">Size: {showSize()}</div>
            <div className="z-body1">Length: {showLength()}</div>
            <div className="z-body1">Created: {recording.created.substring(0, 16).replace("T", " ")}</div>

            {
              recording.url &&
                <div className="mt-4">
                    <audio controls preload="auto">
                        <source src={recording.url} type="audio/webm"/>
                    </audio>
                </div>
            }

            <div className="w-full flex justtify-end mt-4">
              <CustomButton buttonType="dangerBase" size="sm" style={{width: "unset"}}
                            onClick={() => deleteRecording()}>
                Delete recording
              </CustomButton>
            </div>
          </div>

          {
            recording.status === 3 && renderQueryAi()
          }
        </div>


        <div className="main-half-col bg-indigo-900 p-4 mt-2 rounded-md">
          {showStatus()}
        </div>
      </div>

    </>
  );
};

export default TeamRecording;
